"use memo"

import { AuthContext } from '@urbanx/agx.auth.client'
import {
  AutopilotTaskDto,
  TaskCategoryStage,
  useAssignTaskToUser,
} from '@urbanx/agx.activities.client'
import { ContentLayout, PageLayout } from 'layout'
import {
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import './allTasks.scss'
import {
  AgxColumn,
  AgxEmptyState,
  AgxRow,
  AgxTypography,
  Icon_Clock,
} from '@urbanx/agx.ui-components2'
import { useAutopilotTaskUpdates } from 'hooks/useAutopilotTaskUpdates'
import { TaskRow } from './TaskRow'
import useTaskSearch from './useTaskSearch'
import { useNewTaskNotifier } from './useNewTaskNotifier'
import TaskFilters from './TaskFilters/TaskFilters'
import { TaskFilterState } from './TaskFilters/TaskFiltersState'
import { useGetAssigneeOptions } from './TaskFilters/useGetAssigneeOptions'
import { useGetAgencyOptions } from './TaskFilters/useGetAgencyOptions'
import { useGetAllAgencies } from '@urbanx/agx.agencies.client'
import { useNavigateToTask } from 'utils/tasks/useNavigateToTask'
import { getExchangeTaskFamily } from 'utils/tasks/exchangeTaskIdentity'

interface ColumnConfig {
  name: string
  title: string
  headerAlign: 'left' | 'right' | 'center'
}

const columnConfig: ColumnConfig[] = [
  { name: 'type', title: 'Task Type', headerAlign: 'left' },
  { name: 'agency', title: 'Agency', headerAlign: 'left' },
  { name: 'campaign', title: 'Campaign', headerAlign: 'left' },
  { name: 'assignee', title: 'Assignee', headerAlign: 'center' },
  { name: 'status', title: 'Status', headerAlign: 'left' },
  { name: 'dueDate', title: 'Due', headerAlign: 'right' },
]
const shouldCollapseTask = (task: AutopilotTaskDto) =>
  task.category?.stage === TaskCategoryStage.AgreementCompliance ||
  task.category?.stage === TaskCategoryStage.ContractCompliance ||
  getExchangeTaskFamily(task.category?.taskType) !== undefined

const getCollapsedTaskKey = (task: AutopilotTaskDto) =>
  [
    task.campaignId,
    task.contractId ?? '',
    getExchangeTaskFamily(task.category?.taskType) ?? task.category?.stage,
  ].join(':')

const collapseComplianceTasks = (
  tasks: AutopilotTaskDto[]
): AutopilotTaskDto[] => {
  const result: AutopilotTaskDto[] = []
  const seenTasks = new Set<string>()

  for (const task of tasks) {
    if (shouldCollapseTask(task) && task.campaignId) {
      const key = getCollapsedTaskKey(task)
      if (seenTasks.has(key)) {
        continue
      }

      seenTasks.add(key)

      result.push(task)
      continue
    }

    result.push(task)
  }

  return result
}

const TasksTable = () => {
  const [filters, setFilters] = useState<TaskFilterState | undefined>(undefined)
  const loaderRef = useRef<HTMLDivElement | null>(null)
  const { userAccount } = useContext(AuthContext)

  const { assigneeOptions, assigneeOptionsPending } = useGetAssigneeOptions(
    filters?.assignedToUserIds
  )

  const { agencyOptions, isAgenciesPending: isAgenciesOptionsPending } =
    useGetAgencyOptions(filters?.agencyIds)

  const { data: agencies, isPending: isAgenciesPending } = useGetAllAgencies()

  const filtersPending =
    isAgenciesOptionsPending || assigneeOptionsPending || isAgenciesPending

  const {
    hasMore,
    totalCount,
    tasks,
    searchAllTasksPending,
    loadMore,
    handleTaskUpdate,
    refreshSearch,
  } = useTaskSearch(filters, filtersPending)

  useAutopilotTaskUpdates(handleTaskUpdate)
  useNewTaskNotifier(refreshSearch)

  const rows = collapseComplianceTasks(tasks)
  const userName = [userAccount?.firstName, userAccount?.lastName]
    .filter((namePart) => namePart != null && namePart.length > 0)
    .join(' ')

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0]
        if (first.isIntersecting && hasMore && !searchAllTasksPending) {
          loadMore()
        }
      },
      { threshold: 1 }
    )
    const current = loaderRef.current
    if (current) observer.observe(current)
    return () => {
      if (current) observer.unobserve(current)
    }
  }, [hasMore, searchAllTasksPending])

  const { assignTaskToUser, assignTaskToUserPending } = useAssignTaskToUser({
    onSuccess: () => {
      // TODO: add some sort of notification on success when design is ready
    },
    onError: () => {
      // TODO: add some sort of notification on error when design is ready
    },
  })

  const onAssignTaskToUser = async (
    userId?: string,
    taskId?: string
  ) => {
    if (!userId || !taskId || assignTaskToUserPending) return

    const assignedTask = tasks.find((t) => t.id === taskId)
    if (!assignedTask) return

    const siblingIds = shouldCollapseTask(assignedTask)
      ? tasks
          .filter(
            (t) =>
              t.campaignId === assignedTask.campaignId &&
              t.contractId === assignedTask.contractId &&
              getCollapsedTaskKey(t) === getCollapsedTaskKey(assignedTask) &&
              !!t.id
          )
          .map((t) => t.id!)
      : [taskId]

    for (const id of siblingIds) {
      await assignTaskToUser({ taskId: id, userId })
    }
  }

  const navigateToTask = useNavigateToTask()

  return (
    <PageLayout
      agentName={userName}
      primaryPageTitle="All Tasks"
      pageIcon={
        <div className="iconContainer">
          <Icon_Clock height={28} width={28} color="var(--fg-white)" />
        </div>
      }
      header={
        <TaskFilters
          totalCount={totalCount}
          onFilterChange={setFilters}
          pending={assigneeOptionsPending || isAgenciesOptionsPending}
          agencyOptions={agencyOptions}
          assigneeOptions={assigneeOptions}
        />
      }
    >
      <ContentLayout>
        <AgxRow fill>
          <AgxColumn fill gap="spacing-xl">
            <table className="tasksTable">
              <thead>
                <tr>
                  {columnConfig.map(({ name, title, headerAlign }) => (
                    <td
                      key={name}
                      align={headerAlign}
                      className="TasksTableHeaderStyle"
                    >
                      <AgxTypography
                        id={`header-${name}`}
                        size="small"
                        type={'body_ui_strong'}
                      >
                        {title}
                      </AgxTypography>
                    </td>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rowIdx) => (
                  <TaskRow
                    key={row.id}
                    task={row}
                    rowIdx={rowIdx}
                    assigneeOptions={assigneeOptions}
                    agencies={agencies ?? []}
                    onAssignTask={onAssignTaskToUser}
                    onNavigate={navigateToTask}
                  />
                ))}
                {rows.length === 0 &&
                  totalCount !== undefined &&
                  !searchAllTasksPending && (
                    <tr className="emptyStateRow">
                      {' '}
                      {/** Make sure to give className to a row that contains AgxEmptyState, this is to avoid the hover state */}
                      <td colSpan={6} className="noBorder">
                        <AgxEmptyState
                          actionButtons={[]}
                          glyphType="checklistSmile"
                          variant="advanced"
                          label="No task found."
                        />
                      </td>
                    </tr>
                  )}
              </tbody>
            </table>
            {/* Loader indicator */}
            <div ref={loaderRef} style={{ textAlign: 'center', padding: 16 }}>
              {searchAllTasksPending ? 'Loading...' : ''}
            </div>
          </AgxColumn>
        </AgxRow>
      </ContentLayout>
    </PageLayout>
  )
}

export default TasksTable
